import { create } from 'zustand';
import { useNodeStore } from './nodeStore';
import { useGroupStore } from './groupStore';
import { NodeType } from '../types';

interface HandlePosition {
  handleId: string;
  x: number;
  y: number;
}

interface SelectionState {
  selectedNodeIds: Set<string>;
  activeGroupId: string | null;

  selectNode: (id: string, multi?: boolean) => void;
  deselectNode: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  deleteSelected: () => void;
  setSelectedNodeIds: (ids: string[]) => void;
  setActiveGroupId: (groupId: string | null) => void;
  isSelected: (id: string) => boolean;
  getSelectedArray: () => string[];
  getSelectedCount: () => number;

  mouseX: number;
  mouseY: number;
  magneticZoom: number;
  activeHandleId: string | null;
  handlePositions: Map<string, HandlePosition>;
  isConnecting: boolean;

  isBoxSelecting: boolean;
  setBoxSelecting: (v: boolean) => void;

  setMousePosition: (x: number, y: number) => void;
  setMagneticZoom: (zoom: number) => void;
  registerHandle: (handleId: string, x: number, y: number) => void;
  unregisterHandle: (handleId: string) => void;
  setActiveHandle: (handleId: string | null) => void;
  setConnecting: (isConnecting: boolean) => void;
  getClosestHandle: (snapRadius: number) => string | null;
  initGlobalListener: () => void;
}

const BASE_SNAP_RADIUS = 30;
const BASE_RELEASE_RADIUS = 50;

let globalListenerAttached = false;

export const useSelectionStore = create<SelectionState>((set, get) => ({
  selectedNodeIds: new Set<string>(),
  activeGroupId: null,

  selectNode: (id, multi = false) => {
    set(state => {
      const newSet = new Set(multi ? state.selectedNodeIds : []);
      if (multi && newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      const node = useNodeStore.getState().getNodeById(id);
      const activeGroupId = node?.type === NodeType.GROUP ? id : null;
      return { selectedNodeIds: newSet, activeGroupId };
    });
    const node = useNodeStore.getState().getNodeById(id);
    const activeGroupId = node?.type === NodeType.GROUP ? id : null;
    useGroupStore.getState().setActiveGroup(activeGroupId);
  },

  deselectNode: (id) => {
    set(state => {
      const newSet = new Set(state.selectedNodeIds);
      newSet.delete(id);
      return { selectedNodeIds: newSet };
    });
  },

  clearSelection: () => {
    set({ selectedNodeIds: new Set<string>(), activeGroupId: null });
    useGroupStore.getState().setActiveGroup(null);
  },

  selectAll: () => {
    const allNodeIds = useNodeStore.getState().nodes.map(n => n.id);
    set({ selectedNodeIds: new Set(allNodeIds) });
  },

  deleteSelected: () => {
    const { selectedNodeIds } = get();
    if (selectedNodeIds.size === 0) return;
    useNodeStore.getState().deleteNodes(Array.from(selectedNodeIds));
    set({ selectedNodeIds: new Set<string>(), activeGroupId: null });
    useGroupStore.getState().setActiveGroup(null);
  },

  setSelectedNodeIds: (ids) => {
    const newSet = new Set(ids);
    const firstId = ids[0];
    const node = firstId ? useNodeStore.getState().getNodeById(firstId) : null;
    const activeGroupId = node?.type === NodeType.GROUP ? firstId : null;
    set({ selectedNodeIds: newSet, activeGroupId });
    useGroupStore.getState().setActiveGroup(activeGroupId);
  },

  setActiveGroupId: (groupId) => {
    set({ activeGroupId: groupId });
  },

  isSelected: (id) => {
    return get().selectedNodeIds.has(id);
  },

  getSelectedArray: () => {
    return Array.from(get().selectedNodeIds);
  },

  getSelectedCount: () => {
    return get().selectedNodeIds.size;
  },

  mouseX: 0,
  mouseY: 0,
  magneticZoom: 1,
  activeHandleId: null,
  handlePositions: new Map(),
  isConnecting: false,
  isBoxSelecting: false,
  setBoxSelecting: (v: boolean) => set({ isBoxSelecting: v }),

  initGlobalListener: () => {
    if (globalListenerAttached) return;
    globalListenerAttached = true;

    let rafId: number | null = null;
    let pendingX = 0;
    let pendingY = 0;

    const handler = (e: MouseEvent) => {
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          get().setMousePosition(pendingX, pendingY);
        });
      }
    };
    window.addEventListener('mousemove', handler);
  },

  setMousePosition: (x: number, y: number) => {
    const state = get();
    set({ mouseX: x, mouseY: y });

    if (state.isConnecting) return;

    const snapRadius = BASE_SNAP_RADIUS * state.magneticZoom;
    const closest = get().getClosestHandle(snapRadius);

    if (state.activeHandleId) {
      const activePos = state.handlePositions.get(state.activeHandleId);
      if (activePos) {
        const dx = x - activePos.x;
        const dy = y - activePos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        const releaseRadius = BASE_RELEASE_RADIUS * state.magneticZoom;
        if (distance > releaseRadius) {
          set({ activeHandleId: null });
        }
      }
    } else if (closest) {
      set({ activeHandleId: closest });
    }
  },

  setMagneticZoom: (zoom: number) => {
    set({ magneticZoom: zoom });
  },

  registerHandle: (handleId: string, x: number, y: number) => {
    const state = get();
    const newPositions = new Map(state.handlePositions);
    newPositions.set(handleId, { handleId, x, y });
    set({ handlePositions: newPositions });
  },

  unregisterHandle: (handleId: string) => {
    const state = get();
    const newPositions = new Map(state.handlePositions);
    newPositions.delete(handleId);

    if (state.activeHandleId === handleId) {
      set({ handlePositions: newPositions, activeHandleId: null });
    } else {
      set({ handlePositions: newPositions });
    }
  },

  setActiveHandle: (handleId: string | null) => {
    set({ activeHandleId: handleId });
  },

  setConnecting: (isConnecting: boolean) => {
    set({ isConnecting, activeHandleId: isConnecting ? get().activeHandleId : null });
  },

  getClosestHandle: (snapRadius: number) => {
    const state = get();
    const { mouseX, mouseY, handlePositions, isConnecting } = state;

    if (isConnecting) return null;

    let closestId: string | null = null;
    let closestDistance = snapRadius;

    handlePositions.forEach((pos) => {
      const dx = mouseX - pos.x;
      const dy = mouseY - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = pos.handleId;
      }
    });

    return closestId;
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}

export { BASE_SNAP_RADIUS, BASE_RELEASE_RADIUS };
